'use client';

import { useActionState } from 'react';
import { useFormStatus } from 'react-dom';
import TurnstileClient from '@/components/TurnstileClient';
import { submitLeadMagnetAction, type LeadMagnetState } from '@/app/actions/submitLeadMagnet';

interface LeadMagnetFormProps {
    /** Ziel-Pfad der Danke-Seite (aus Storyblok Redirect aufgelöst). */
    redirectTarget: string;
    /** Titel des Factsheets (LeadMagnetName) – wird als Brevo-Attribut FACTSHEET gespeichert. */
    leadMagnetName?: string;
    /** Numerische Factsheet-ID (1, 2, 3 …) – wird als Brevo-Attribut FACTSHEET_ID gespeichert. */
    factsheetId?: string;
    /** Brevo Listen-ID (hidden). */
    brevoListId?: string;
    /** Cloudflare Turnstile Site-Key (optional). */
    siteKey?: string;
    /** Umami-Event-Label. */
    eventLabel?: string;
}

const initialState: LeadMagnetState = { ok: false };

function SubmitButton({ eventLabel }: { eventLabel?: string }) {
    const { pending } = useFormStatus();
    return (
        <button
            data-umami-event={eventLabel || 'formular-submit-leadmagnet'}
            type="submit"
            disabled={pending}
            className="main-button main-button--secondary w-full max-w-xs disabled:opacity-60"
        >
            <span className="text-white mx-auto">
                {pending ? 'Wird gesendet...' : 'Factsheet herunterladen'}
            </span>
        </button>
    );
}

export default function LeadMagnetForm({
    redirectTarget,
    leadMagnetName,
    factsheetId,
    brevoListId,
    siteKey,
    eventLabel,
}: LeadMagnetFormProps) {
    const [state, formAction] = useActionState(submitLeadMagnetAction, initialState);

    return (
        <form action={formAction} className="flex flex-col lg:grid lg:grid-cols-2 gap-5 mt-10 max-w-xl">
            <div>
                <label htmlFor="lm-name" className="block text-sm font-medium text-white mb-1">
                    Name
                </label>
                <input
                    type="text"
                    id="lm-name"
                    name="name"
                    placeholder="Name"
                    required
                    className="mt-1 py-3 px-4 bg-neutral-700/75 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                />
            </div>
            <div>
                <label htmlFor="lm-phone" className="block text-sm font-medium text-white mb-1">
                    Telefon (optional)
                </label>
                <input
                    type="tel"
                    id="lm-phone"
                    name="phone"
                    placeholder="Telefon"
                    className="mt-1 py-3 px-4 bg-neutral-700/75 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                />
            </div>
            <div className="col-span-2">
                <label htmlFor="lm-email" className="block text-sm font-medium text-white mb-1">
                    E-Mail Adresse
                </label>
                <input
                    type="email"
                    id="lm-email"
                    name="email"
                    placeholder="Ihre geschäftliche E-Mail Adresse"
                    required
                    className="mt-1 py-3 px-4 bg-neutral-700/75 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                />
            </div>

            {/* Hidden-Felder – nicht im Frontend sichtbar, werden aber an die API übertragen. */}
            <input type="hidden" name="Weiterleitung" value={redirectTarget} />
            {leadMagnetName && <input type="hidden" name="factsheet" value={leadMagnetName} />}
            {factsheetId && <input type="hidden" name="factsheetId" value={factsheetId} />}
            {brevoListId && <input type="hidden" name="BREVO_LIST_ID" value={brevoListId} />}

            {siteKey && (
                <div className="col-span-2">
                    <TurnstileClient siteKey={siteKey} />
                </div>
            )}

            {state.error && (
                <p className="col-span-2 text-sm text-red-400" role="alert">
                    {state.error}
                </p>
            )}

            <div className="col-span-2">
                <SubmitButton eventLabel={eventLabel} />
            </div>
        </form>
    );
}
